import type {
  LocalizedSpeechPreviewRequest,
  LocalizedSpeechPreviewResponseData,
} from '@/shared/api/types/localized-speech-types'
import type { TranslationJobScope, TranslationJobStatus } from '@/shared/constants/status'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import i18next from 'i18next'

import {
  createLocalizationJob,
  deleteLocalizationJob,
  getLocalizationJob,
  StreamFatalError,
  streamLocalizationJob,
} from '@/shared/api/services/localized-speech-service'
import { TRANSLATION_JOB_STATUS } from '@/shared/constants/status'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { apiLogger } from '@/shared/libs/logger'

import { showToast } from '@/shared/libs/toast'

export interface TranslationJob {
  id: string
  backendJobId?: string
  scope: TranslationJobScope
  entityId: string
  entityLabel?: string
  entityPath?: string
  outputLanguages: string[]
  payload: LocalizedSpeechPreviewRequest
  sourceTranslations?: unknown
  restoreRequested?: boolean
  status: TranslationJobStatus
  startedAt: number
  finishedAt?: number
  completedLanguages?: string[]
  result?: LocalizedSpeechPreviewResponseData
  error?: string
  consumed: boolean
}

interface StartTranslationJobInput {
  scope: TranslationJobScope
  entityId: string
  entityLabel?: string
  entityPath?: string
  payload: LocalizedSpeechPreviewRequest
  sourceTranslations?: unknown
}

export interface StartTranslationJobResult {
  jobId: string
}

interface TranslationJobsState {
  jobs: TranslationJob[]
  drafts: Record<string, unknown>
  startJob: (input: StartTranslationJobInput) => StartTranslationJobResult
  setDraft: (scope: TranslationJobScope, entityId: string, draft: unknown) => void
  clearDraft: (scope: TranslationJobScope, entityId: string) => void
  clearEntityJobs: (scope: TranslationJobScope, entityId: string) => void
  markJobForApply: (jobId: string) => void
  requestJobRestore: (jobId: string) => void
  consumeJobRestore: (jobId: string) => void
  consumeJob: (jobId: string) => void
  dismissJob: (jobId: string) => void
  clearFinishedJobs: () => void
  clearAllJobs: () => void
}

const streamCleanups = new Map<string, () => void>()

export const useTranslationJobsStore = create<TranslationJobsState>()(
  persist(
    (set) => ({
      jobs: [],
      drafts: {},
      // ? VI: Input gồm metadata job + payload dịch. | EN: Input includes job metadata + translation payload.
      // ? VI: Output trả về accepted/rejected và jobId (nếu tạo thành công). | EN: Output returns accepted/rejected and jobId (when created).
      // * VI: Mục đích tạo và theo dõi tác vụ dịch/audio ở backend job. | EN: Purpose is to create and track backend translation/audio jobs.
      startJob: (input) => {
        const jobId = crypto.randomUUID()
        const job: TranslationJob = {
          id: jobId,
          scope: input.scope,
          entityId: input.entityId,
          ...(input.entityLabel ? { entityLabel: input.entityLabel } : {}),
          ...(input.entityPath ? { entityPath: input.entityPath } : {}),
          outputLanguages: input.payload.outputLanguages,
          payload: input.payload,
          ...(input.sourceTranslations !== undefined
            ? { sourceTranslations: input.sourceTranslations }
            : {}),
          status: TRANSLATION_JOB_STATUS.PROCESSING,
          startedAt: Date.now(),
          completedLanguages: [],
          consumed: false,
        }

        set((state) => ({ jobs: [job, ...state.jobs] }))
        showToast.info(
          input.payload.audioEnabled
            ? i18next.t('jobs.toastQueuedWithAudio')
            : i18next.t('jobs.toastQueuedTextOnly'),
        )
        void submitBackendJob(jobId, input.payload)

        return { jobId }
      },
      setDraft: (scope, entityId, draft) => {
        const key = buildTranslationJobEntityKey(scope, entityId)
        set((state) => ({
          drafts: {
            ...state.drafts,
            [key]: draft,
          },
        }))
      },
      clearDraft: (scope, entityId) => {
        const key = buildTranslationJobEntityKey(scope, entityId)
        set((state) => {
          if (!(key in state.drafts)) {
            return state
          }
          const nextDrafts = { ...state.drafts }
          delete nextDrafts[key]
          return { drafts: nextDrafts }
        })
      },
      clearEntityJobs: (scope, entityId) => {
        const key = buildTranslationJobEntityKey(scope, entityId)
        const jobsToRemove = useTranslationJobsStore
          .getState()
          .jobs.filter((job) => job.scope === scope && job.entityId === entityId)
        set((state) => {
          const nextDrafts = { ...state.drafts }
          delete nextDrafts[key]
          return {
            drafts: nextDrafts,
            jobs: state.jobs.filter((job) => !(job.scope === scope && job.entityId === entityId)),
          }
        })
        jobsToRemove.forEach(removeJobTracking)
      },
      // ? VI: Input là job id đã hoàn thành. | EN: Input is a completed job id.
      // ? VI: Output đánh dấu consumed để UI không apply lặp lại. | EN: Output marks consumed to prevent re-applying in UI.
      consumeJob: (jobId) => {
        set((state) => ({
          jobs: state.jobs.map((job) => (job.id === jobId ? { ...job, consumed: true } : job)),
        }))
      },
      markJobForApply: (jobId) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId && job.status === TRANSLATION_JOB_STATUS.SUCCESS
              ? { ...job, consumed: false }
              : job,
          ),
        }))
      },
      requestJobRestore: (jobId) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId && job.status === TRANSLATION_JOB_STATUS.PROCESSING
              ? { ...job, restoreRequested: true }
              : { ...job, restoreRequested: false },
          ),
        }))
      },
      consumeJobRestore: (jobId) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId ? { ...job, restoreRequested: false } : job,
          ),
        }))
      },
      // ? VI: Input là bất kỳ job id nào. | EN: Input is any job id.
      // ? VI: Output xóa job khỏi panel. | EN: Output removes the job card from the panel.
      dismissJob: (jobId) => {
        const job = useTranslationJobsStore.getState().jobs.find((item) => item.id === jobId)
        set((state) => ({ jobs: state.jobs.filter((job) => job.id !== jobId) }))
        if (job) {
          removeJobTracking(job)
        }
      },
      // ? VI: Input không có tham số. | EN: Input has no arguments.
      // ? VI: Output chỉ giữ job đang chạy, xóa hết success/error/cancelled khỏi panel. | EN: Output keeps only running jobs; removes all finished jobs from the panel.
      clearFinishedJobs: () => {
        const jobsToRemove = useTranslationJobsStore
          .getState()
          .jobs.filter((job) => job.status !== TRANSLATION_JOB_STATUS.PROCESSING)
        set((state) => ({
          jobs: state.jobs.filter((job) => job.status === TRANSLATION_JOB_STATUS.PROCESSING),
        }))
        jobsToRemove.forEach(removeJobTracking)
      },
      // ? VI: Input không có tham số. | EN: Input has no arguments.
      // ? VI: Output dừng mọi stream và xóa job/draft khỏi store khi rời phiên đăng nhập. | EN: Stops streams and clears job/draft state when leaving the session.
      clearAllJobs: () => {
        const jobsToRemove = useTranslationJobsStore.getState().jobs
        set({ jobs: [], drafts: {} })
        jobsToRemove.forEach(removeJobTracking)
        streamCleanups.forEach((cleanup) => cleanup())
        streamCleanups.clear()
      },
    }),
    {
      name: 'translation-jobs-store',
      partialize: (state) => ({
        drafts: state.drafts,
        jobs: state.jobs.map((job) => ({
          ...job,
          consumed: job.status === TRANSLATION_JOB_STATUS.SUCCESS ? job.consumed : false,
        })),
      }),
      onRehydrateStorage: () => () => {
        queueMicrotask(() => {
          resumeRunningJobs()
        })
      },
    },
  ),
)

export function buildTranslationJobEntityKey(scope: TranslationJobScope, entityId: string) {
  return `${scope}:${entityId}`
}

function getJobTitle(job: TranslationJob) {
  return job.entityLabel ?? buildTranslationJobEntityKey(job.scope, job.entityId)
}

function getTranslationJobErrorText(error: unknown) {
  if (error instanceof StreamFatalError) {
    return error.status === 404
      ? i18next.t('jobs.errorStreamNotFound')
      : i18next.t('jobs.errorStreamFailed')
  }
  const apiErrorText = getApiErrorText(error)
  if (apiErrorText) {
    return apiErrorText
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return i18next.t('jobs.toastGenericError')
}

function stopStreaming(jobId: string) {
  const cleanup = streamCleanups.get(jobId)
  if (cleanup) {
    cleanup()
    streamCleanups.delete(jobId)
  }
}

function isActiveProcessingJob(jobId: string) {
  const job = useTranslationJobsStore.getState().jobs.find((item) => item.id === jobId)
  return Boolean(job && job.status === TRANSLATION_JOB_STATUS.PROCESSING)
}

function removeJobTracking(job: TranslationJob) {
  deleteBackendJob(job)
  stopStreaming(job.id)
}

function deleteBackendJob(job: TranslationJob) {
  if (!job.backendJobId) {
    return
  }

  void deleteLocalizationJob(job.backendJobId)
    .then((status) => {
      if (status === 200) {
        showToast.success(i18next.t('jobs.toastDeleted', { name: getJobTitle(job) }))
      }
    })
    .catch((error: unknown) => {
      apiLogger.error('Failed to delete localization preview job', {
        jobId: job.id,
        backendJobId: job.backendJobId,
        error,
      })
    })
}

function markJobError(jobId: string, error: unknown) {
  completeJob(jobId, {
    status: TRANSLATION_JOB_STATUS.FAILED,
    error: getTranslationJobErrorText(error),
  })
}

function applyJobResult(
  jobId: string,
  data: LocalizedSpeechPreviewResponseData,
  completedLanguages = Object.keys(data),
) {
  if (!isActiveProcessingJob(jobId)) {
    return
  }
  useTranslationJobsStore.setState((state) => ({
    jobs: state.jobs.map((job) =>
      job.id === jobId && job.status === TRANSLATION_JOB_STATUS.PROCESSING
        ? {
            ...job,
            completedLanguages: Array.from(
              new Set([...(job.completedLanguages ?? []), ...completedLanguages]),
            ),
            result: { ...job.result, ...data },
          }
        : job,
    ),
  }))
}

function completeJob(
  jobId: string,
  outcome:
    | { status: typeof TRANSLATION_JOB_STATUS.SUCCESS; result: LocalizedSpeechPreviewResponseData }
    | { status: typeof TRANSLATION_JOB_STATUS.FAILED; error: string },
) {
  if (!isActiveProcessingJob(jobId)) {
    stopStreaming(jobId)
    return
  }
  let nextJob: TranslationJob | undefined
  useTranslationJobsStore.setState((state) => {
    const nextJobs = state.jobs.map((job) => {
      if (job.id !== jobId || job.status !== TRANSLATION_JOB_STATUS.PROCESSING) {
        return job
      }
      const finishedAt = Date.now()
      nextJob =
        outcome.status === TRANSLATION_JOB_STATUS.SUCCESS
          ? {
              ...job,
              status: TRANSLATION_JOB_STATUS.SUCCESS,
              finishedAt,
              result: outcome.result,
            }
          : {
              ...job,
              status: TRANSLATION_JOB_STATUS.FAILED,
              finishedAt,
              error: outcome.error,
            }
      return nextJob
    })
    return { jobs: nextJobs }
  })
  stopStreaming(jobId)
  if (nextJob?.status === TRANSLATION_JOB_STATUS.SUCCESS) {
    showToast.success(i18next.t('jobs.toastCompleted', { name: getJobTitle(nextJob) }))
  } else if (nextJob) {
    showToast.error(i18next.t('jobs.toastFailed', { name: getJobTitle(nextJob) }))
  }
}

function attachStream(jobId: string) {
  if (!isActiveProcessingJob(jobId)) {
    stopStreaming(jobId)
    return
  }
  const job = useTranslationJobsStore.getState().jobs.find((item) => item.id === jobId)
  if (!job?.backendJobId) {
    return
  }

  stopStreaming(jobId)

  const isStale = () => !isActiveProcessingJob(jobId)

  const cleanup = streamLocalizationJob(job.backendJobId, {
    onSnapshot: (data, status) => {
      if (isStale()) return
      if (status === TRANSLATION_JOB_STATUS.SUCCESS) {
        completeJob(jobId, { status: TRANSLATION_JOB_STATUS.SUCCESS, result: data })
      } else if (status === TRANSLATION_JOB_STATUS.FAILED) {
        completeJob(jobId, {
          status: TRANSLATION_JOB_STATUS.FAILED,
          error: i18next.t('jobs.toastGenericError'),
        })
      } else {
        applyJobResult(jobId, data)
      }
    },
    onLanguageResult: (language, item) => {
      if (isStale()) return
      const audioUrl = item.speechUrl ?? item.audioUrl ?? undefined
      applyJobResult(
        jobId,
        {
          [language]: {
            fields: item.fields ?? {},
            ...(audioUrl ? { speechUrl: audioUrl, audioUrl } : {}),
          },
        },
        [language],
      )
    },
    onCompleted: (data) => {
      if (isStale()) return
      completeJob(jobId, { status: TRANSLATION_JOB_STATUS.SUCCESS, result: data })
    },
    onFailed: (error) => {
      if (isStale()) return
      completeJob(jobId, {
        status: TRANSLATION_JOB_STATUS.FAILED,
        error: error || i18next.t('jobs.toastGenericError'),
      })
    },
    onCancelled: () => {
      if (isStale()) return
      completeJob(jobId, {
        status: TRANSLATION_JOB_STATUS.FAILED,
        error: i18next.t('jobs.errorCancelled'),
      })
    },
    onTimeout: () => {
      // The stream auto-reconnects and will emit a fresh snapshot; no state change needed here.
    },
    onStreamError: (error) => {
      if (isStale()) return
      markJobError(jobId, error)
    },
  })

  streamCleanups.set(jobId, cleanup)
}

async function resolveImmediateTerminalJob(jobId: string, backendJobId: string) {
  try {
    const response = await getLocalizationJob(backendJobId)
    if (!isActiveProcessingJob(jobId)) {
      return
    }
    if (response.data.status === TRANSLATION_JOB_STATUS.SUCCESS) {
      completeJob(jobId, {
        status: TRANSLATION_JOB_STATUS.SUCCESS,
        result: response.data.data ?? {},
      })
    } else if (response.data.status === TRANSLATION_JOB_STATUS.FAILED) {
      completeJob(jobId, {
        status: TRANSLATION_JOB_STATUS.FAILED,
        error: response.data.error ?? i18next.t('jobs.toastGenericError'),
      })
    } else {
      attachStream(jobId)
    }
  } catch (error) {
    markJobError(jobId, error)
  }
}

async function submitBackendJob(jobId: string, payload: LocalizedSpeechPreviewRequest) {
  try {
    const response = await createLocalizationJob(payload)
    if (!isActiveProcessingJob(jobId)) {
      return
    }
    useTranslationJobsStore.setState((state) => {
      const nextJobs = state.jobs.map((job) => {
        if (job.id !== jobId || job.status !== TRANSLATION_JOB_STATUS.PROCESSING) {
          return job
        }
        return {
          ...job,
          backendJobId: response.data.jobId,
        }
      })
      return { jobs: nextJobs }
    })

    if (
      response.data.status === TRANSLATION_JOB_STATUS.SUCCESS ||
      response.data.status === TRANSLATION_JOB_STATUS.FAILED
    ) {
      await resolveImmediateTerminalJob(jobId, response.data.jobId)
      return
    }
    attachStream(jobId)
  } catch (error) {
    markJobError(jobId, error)
  }
}

function resumeRunningJobs() {
  useTranslationJobsStore.getState().jobs.forEach((job) => {
    if (job.status !== TRANSLATION_JOB_STATUS.PROCESSING) {
      stopStreaming(job.id)
      return
    }
    if (job.backendJobId) {
      attachStream(job.id)
      return
    }
    void submitBackendJob(job.id, job.payload)
  })
}
